import { useEffect, useMemo, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Pencil, ShieldCheck, FolderOpen, Phone, Building2, Mail, Home, Send } from "lucide-react";
import { toast } from "sonner";
import TenantDocumentsDialog from "../components/TenantDocumentsDialog";
import AddTenantToPropertyDialog from "../components/AddTenantToPropertyDialog";
import ConvertTenantToLeaseDialog from "../components/ConvertTenantToLeaseDialog";
import { formatUSPhone, telHref } from "../lib/phone";
import USPhoneInput from "../components/USPhoneInput";
import { US_STATES } from "../constants/leaseOptions";

/**
 * Returns a human-readable "last activity" string for a tenant's last_login_at.
 * Goes beyond simple "3d ago" for long-dormant accounts because "847d ago"
 * doesn't help anyone.
 */
function lastActiveLabel(iso) {
  if (!iso) return { label: "Never signed in", tone: "neutral", sortKey: -1 };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { label: "—", tone: "neutral", sortKey: 0 };
  const ms = Date.now() - d.getTime();
  const sortKey = d.getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return { label: "Just now", tone: "good", sortKey };
  if (mins < 60) return { label: `${mins}m ago`, tone: "good", sortKey };
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return { label: `${hrs}h ago`, tone: "good", sortKey };
  const days = Math.floor(hrs / 24);
  if (days === 1) return { label: "Yesterday", tone: "good", sortKey };
  if (days < 7) return { label: `${days}d ago`, tone: "good", sortKey };
  if (days < 30) return { label: `${Math.floor(days / 7)}w ago`, tone: "warn", sortKey };
  if (days < 365) return { label: `${Math.floor(days / 30)}mo ago`, tone: "warn", sortKey };
  return { label: `${Math.floor(days / 365)}y ago`, tone: "bad", sortKey };
}

export default function Tenants() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [leases, setLeases] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [open, setOpen] = useState(false);
  const [addToPropertyOpen, setAddToPropertyOpen] = useState(false);
  const [editing, setEditing] = useState(null);  // null = invite flow, tenant obj = edit flow
  const [docsFor, setDocsFor] = useState(null);
  const [convertingTenant, setConvertingTenant] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", full_name: "", company: "", phone: "", landlord_id: "" });

  // Most recently active tenants first — nulls (never signed in) last so
  // onboarding gaps surface at the bottom of the list, prime candidates for
  // a Resend Welcome click.
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const ta = a.last_login_at ? new Date(a.last_login_at).getTime() : -1;
      const tb = b.last_login_at ? new Date(b.last_login_at).getTime() : -1;
      return tb - ta;
    });
  }, [items]);

  const openInvite = () => {
    setEditing(null);
    setForm({ email: "", password: "", full_name: "", company: "", phone: "", landlord_id: "", address_line1: "", address_line2: "", city: "", state: "", postal_code: "" });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      email: t.email || "",
      password: "", // only send when admin wants to reset
      full_name: t.full_name || "",
      company: t.company || "",
      phone: t.phone || "",
      landlord_id: t.landlord_id || "",
      address_line1: t.address_line1 || "",
      address_line2: t.address_line2 || "",
      city: t.city || "",
      state: t.state || "",
      postal_code: t.postal_code || "",
    });
    setOpen(true);
  };

  const load = async () => {
    const { data } = await api.get("/tenants");
    setItems(data);
    // Load leases too so we can flag prospect tenants (those with no active lease)
    try {
      const { data: ls } = await api.get("/leases");
      setLeases(ls || []);
    } catch {
      setLeases([]);
    }
    if (isAdmin) {
      const { data: ll } = await api.get("/admin/landlords");
      setInvestors(ll);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin]);

  // Prospects = tenants without an active lease today. We surface a badge so
  // landlords can see how many invitees are still un-placed at a glance.
  const prospectIds = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const activeTenantIds = new Set(
      (leases || [])
        .filter((l) => {
          const start = l.start_date || "";
          const end = l.end_date || "";
          return start && end && start <= today && today <= end;
        })
        .map((l) => l.tenant_id)
        .filter(Boolean),
    );
    return new Set(items.filter((t) => !activeTenantIds.has(t.id)).map((t) => t.id));
  }, [items, leases]);
  const prospectCount = prospectIds.size;

  const save = async (e) => {
    e.preventDefault();
    if (!editing && isAdmin && !form.landlord_id) {
      toast.error("Select a investor to assign this tenant to");
      return;
    }
    try {
      if (editing) {
        const payload = {
          email: form.email,
          full_name: form.full_name,
          company: form.company,
          phone: form.phone,
          address_line1: form.address_line1,
          address_line2: form.address_line2,
          city: form.city,
          state: form.state,
          postal_code: form.postal_code,
        };
        if (form.password && form.password.trim()) payload.password = form.password.trim();
        await api.put(`/tenants/${editing.id}`, payload);
        toast.success(`${form.full_name} updated${payload.password ? " · password reset" : ""}`);
      } else {
        const payload = { ...form };
        if (!isAdmin) delete payload.landlord_id;
        const { data: created } = await api.post("/tenants", payload);
        const emailed = created?.welcome_email_sent;
        const emailStatus = created?.welcome_email_status;
        if (emailed) {
          toast.success(`${form.full_name} invited — welcome email sent to ${form.email}`);
        } else if (emailStatus === "mocked") {
          toast.message(`${form.full_name} invited (MOCK email mode)`, {
            description: `Share credentials manually · ${form.email} / ${form.password}`,
          });
        } else {
          toast.warning(`${form.full_name} invited — welcome email failed to send`, {
            description: `Please share manually · ${form.email} / ${form.password}`,
          });
        }
      }
      setOpen(false);
      setEditing(null);
      setForm({ email: "", password: "", full_name: "", company: "", phone: "", landlord_id: "", address_line1: "", address_line2: "", city: "", state: "", postal_code: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this tenant and their leases?")) return;
    await api.delete(`/tenants/${id}`);
    toast.success("Tenant removed");
    load();
  };

  const resendWelcome = async (tenant) => {
    const ok = window.confirm(
      `Resend the welcome email to ${tenant.full_name} (${tenant.email})?\n\n` +
      `A NEW temporary password will be generated — their current password will stop working.`,
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/tenants/${tenant.id}/resend-welcome`);
      if (data.welcome_email_sent) {
        toast.success(`Welcome email sent to ${tenant.email}`, {
          description: `New temp password generated.`,
        });
      } else if (data.welcome_email_status === "mocked") {
        toast.message(`MOCK email mode — share manually`, {
          description: `${tenant.email} / ${data.temp_password}`,
        });
      } else {
        toast.warning(`Welcome email failed — share manually`, {
          description: `${tenant.email} / ${data.temp_password}`,
        });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Resend failed");
    }
  };

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline text-slate-500 flex items-center gap-2">
              Directory
              {isAdmin && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-900 text-white rounded-sm text-[10px]" data-testid="admin-badge">
                  <ShieldCheck className="w-3 h-3" /> ADMIN
                </span>
              )}
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"Outfit"}}>Tenants</h1>
            <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
              <span>
                {items.length} tenants {isAdmin ? "across all platform users" : "connected to your portal"}.
              </span>
              {prospectCount > 0 && (
                <span
                  data-testid="prospects-badge"
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-sm text-[11px] font-mono uppercase tracking-widest"
                  title="Tenants without an active lease today — click ‘Convert to lease’ on a row to place them"
                >
                  <Home className="w-3 h-3" /> {prospectCount} prospect{prospectCount === 1 ? "" : "s"}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="add-tenant-to-property-button"
              onClick={() => setAddToPropertyOpen(true)}
              className="btn-outline-dark flex items-center gap-2 text-sm"
              title="One-step onboarding: creates tenant AND their lease together"
            >
              <Building2 className="w-4 h-4" /> Add to property
            </button>
            <button data-testid="add-tenant-button" onClick={openInvite} className="btn-primary-dark flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" /> Create Tenant
            </button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid="tenants-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Last active</Th>
                {isAdmin && <Th>Investor</Th>}
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={isAdmin ? 5 : 4} className="text-center py-16 text-slate-400">No tenants yet.</td></tr>}
              {sortedItems.map((t) => {
                const la = lastActiveLabel(t.last_login_at);
                return (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`tenant-row-${t.id}`}>
                  <Td className="font-semibold text-slate-900">
                    <div>{t.full_name}</div>
                    {t.company && (
                      <div
                        className="text-xs text-slate-500 font-normal mt-0.5"
                        data-testid={`tenant-company-${t.id}`}
                      >
                        {t.company}
                      </div>
                    )}
                  </Td>
                  <Td className="text-slate-600">{t.email}</Td>
                  <Td className="text-slate-600 font-mono text-xs">
                    {t.phone ? (
                      telHref(t.phone) ? (
                        <a
                          href={telHref(t.phone)}
                          className="inline-flex items-center gap-1 hover:text-slate-900 hover:underline"
                          data-testid={`tenant-phone-link-${t.id}`}
                          title="Click to dial"
                        >
                          <Phone className="w-3 h-3" /> {formatUSPhone(t.phone)}
                        </a>
                      ) : (
                        <span>{formatUSPhone(t.phone)}</span>
                      )
                    ) : "—"}
                  </Td>
                  <Td>
                    <span
                      data-testid={`tenant-last-active-${t.id}`}
                      className={
                        la.tone === "good"   ? "text-green-700 text-xs font-semibold" :
                        la.tone === "warn"   ? "text-amber-700 text-xs font-semibold" :
                        la.tone === "bad"    ? "text-red-600 text-xs font-semibold"   :
                                               "text-slate-400 text-xs italic"
                      }
                      title={t.last_login_at ? new Date(t.last_login_at).toLocaleString() : "Tenant has never signed in"}
                    >
                      {la.label}
                    </span>
                  </Td>
                  {isAdmin && (
                    <Td className="text-slate-600">
                      <div className="text-sm">{t.landlord_name || "—"}</div>
                      {t.landlord_company && <div className="text-xs text-slate-400">{t.landlord_company}</div>}
                    </Td>
                  )}
                  <Td className="text-right">
                    <button onClick={() => setConvertingTenant(t)} className="text-slate-500 hover:text-emerald-600 p-1 mr-1" data-testid={`convert-tenant-${t.id}`} title="Convert to lease — place this tenant on a property">
                      <Home className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => resendWelcome(t)}
                      disabled={!t.email}
                      className="inline-flex items-center gap-1 text-xs font-semibold border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed px-2.5 py-1 rounded-sm mr-2"
                      data-testid={`resend-welcome-${t.id}`}
                      title={t.email ? `Email portal login URL + a fresh temp password to ${t.email}` : "No email on file"}
                    >
                      <Send className="w-3.5 h-3.5" /> Send login
                    </button>
                    <button onClick={() => setDocsFor(t)} className="text-slate-500 hover:text-slate-900 p-1 mr-1" data-testid={`docs-tenant-${t.id}`} title="Documents">
                      <FolderOpen className="w-4 h-4" />
                    </button>
                    <button onClick={() => openEdit(t)} className="text-slate-500 hover:text-slate-900 p-1 mr-1" data-testid={`edit-tenant-${t.id}`} title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(t.id)} className="text-slate-500 hover:text-red-600 p-1" data-testid={`delete-tenant-${t.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </Td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{fontFamily:"Outfit"}}>{editing ? "Edit tenant" : "Create tenant"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-3">
              {isAdmin && !editing && (
                <Field label="Assign to investor">
                  <select required className="input-clean w-full" data-testid="tenant-landlord-select" value={form.landlord_id} onChange={(e)=>setForm({...form, landlord_id:e.target.value})}>
                    <option value="">Select investor</option>
                    {investors.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.full_name}{l.company ? ` — ${l.company}` : ""} ({l.email})
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label="Full name"><input required className="input-clean w-full" data-testid="tenant-name-input" value={form.full_name} onChange={(e)=>setForm({...form, full_name:e.target.value})} /></Field>
              <Field label="Email"><input required type="email" className="input-clean w-full" data-testid="tenant-email-input" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} /></Field>
              <Field label={editing ? "Reset password (optional — leave blank to keep current)" : "Temporary password"}>
                <input
                  {...(editing ? {} : { required: true })}
                  className="input-clean w-full"
                  data-testid="tenant-pass-input"
                  value={form.password}
                  onChange={(e)=>setForm({...form, password:e.target.value})}
                  minLength={editing ? 0 : 8}
                  type={editing ? "password" : "text"}
                  autoComplete={editing ? "new-password" : undefined}
                  placeholder={editing ? "Leave blank to keep current password" : ""}
                />
              </Field>
              <Field label="Company"><input className="input-clean w-full" data-testid="tenant-company-input" value={form.company} onChange={(e)=>setForm({...form, company:e.target.value})} /></Field>
              <Field label="Phone"><USPhoneInput data-testid="tenant-phone-input" value={form.phone} onChange={(digits) => setForm({...form, phone: digits})} /></Field>

              <div className="border-t border-slate-100 pt-3 mt-1">
                <div className="overline text-slate-600 mb-2">Mailing address (optional)</div>
                <p className="text-[11px] text-slate-500 mb-3">
                  Used in lease §15 (Notices) on the generated commercial lease draft. State also serves as a tertiary fallback for Governing Law if neither the lease nor the property has a state set.
                </p>
                <Field label="Street address">
                  <input
                    className="input-clean w-full"
                    data-testid="tenant-address-line1-input"
                    value={form.address_line1 || ""}
                    onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
                  />
                </Field>
                <Field label="Apt / suite / unit (optional)">
                  <input
                    className="input-clean w-full"
                    data-testid="tenant-address-line2-input"
                    value={form.address_line2 || ""}
                    onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
                  />
                </Field>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="City">
                    <input
                      className="input-clean w-full"
                      data-testid="tenant-city-input"
                      value={form.city || ""}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                    />
                  </Field>
                  <Field label="State">
                    <select
                      className="input-clean w-full"
                      data-testid="tenant-state-select"
                      value={form.state || ""}
                      onChange={(e) => setForm({ ...form, state: e.target.value })}
                    >
                      <option value="">—</option>
                      {US_STATES.map((s) => (
                        <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="ZIP">
                    <input
                      className="input-clean w-full"
                      data-testid="tenant-postal-code-input"
                      maxLength={10}
                      value={form.postal_code || ""}
                      onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                    />
                  </Field>
                </div>
              </div>

              <DialogFooter>
                <button type="button" onClick={()=>{ setOpen(false); setEditing(null); }} className="btn-outline-dark">Cancel</button>
                <button type="submit" className="btn-primary-dark" data-testid="tenant-submit-button">{editing ? "Save changes" : "Create tenant"}</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <TenantDocumentsDialog
          tenant={docsFor}
          open={!!docsFor}
          onOpenChange={(v) => !v && setDocsFor(null)}
        />

        <AddTenantToPropertyDialog
          open={addToPropertyOpen}
          onOpenChange={setAddToPropertyOpen}
          onCreated={load}
          isAdmin={isAdmin}
          investors={investors}
        />

        <ConvertTenantToLeaseDialog
          tenant={convertingTenant}
          onOpenChange={(v) => !v && setConvertingTenant(null)}
          onCreated={load}
        />
      </div>
    </DashboardShell>
  );
}

function Th({ children, className="" }) { return <th className={`px-5 py-3 overline text-slate-600 ${className}`}>{children}</th>; }
function Td({ children, className="" }) { return <td className={`px-5 py-4 ${className}`}>{children}</td>; }
function Field({ label, children }) { return <label className="block"><div className="overline text-slate-600 mb-1">{label}</div>{children}</label>; }
